/**
 * Sidebar Component
 * Uses shadcn/ui components for consistent styling
 */

import { Link, useLocation } from 'react-router';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { useMyPermissions } from '@/hooks';
import {
    LayoutDashboard,
    Database,
    Settings,
    Users,
    FolderOpen,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';

const mainNavItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'Content', icon: Database, path: '/admin/content' },
    { label: 'Files', icon: FolderOpen, path: '/admin/files' },
    { label: 'Users', icon: Users, path: '/admin/users' }, // Available to app access users
];

const bottomNavItems = [
    { label: 'Settings', icon: Settings, path: '/admin/settings', adminOnly: true },
];

interface NavItemProps {
    item: { label: string; icon: typeof LayoutDashboard; path: string; adminOnly?: boolean };
    isActive: boolean;
    isCollapsed: boolean;
}

function NavItem({ item, isActive, isCollapsed }: NavItemProps) {
    const linkContent = (
        <Link
            to={item.path}
            className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                isCollapsed && 'justify-center px-2'
            )}
        >
            <item.icon className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>{item.label}</span>}
        </Link>
    );

    if (isCollapsed) {
        return (
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    {linkContent}
                </TooltipTrigger>
                <TooltipContent side="right" className="flex items-center gap-2">
                    {item.label}
                </TooltipContent>
            </Tooltip>
        );
    }

    return linkContent;
}

export function Sidebar() {
    const location = useLocation();
    const { sidebarCollapsed, setSidebarCollapsed } = useAppStore();
    const { adminAccess } = useAuthStore();
    const { data: permissions } = useMyPermissions();

    const isActive = (path: string) => {
        if (path === '/admin') {
            return location.pathname === '/admin';
        }
        return location.pathname.startsWith(path);
    };

    // Check if user has access to files
    const hasFilesAccess = adminAccess || permissions?.some((p: any) =>
        p.collection === 'cms_files' && p.action === 'read' && p.access !== 'none'
    );

    // Filter nav items based on admin access and permissions
    const filteredMainNavItems = mainNavItems.filter(item => {
        if (item.path === '/admin/files') {
            return hasFilesAccess;
        }
        return true;
    });
    const filteredBottomNavItems = bottomNavItems.filter(
        item => !item.adminOnly || adminAccess
    );

    return (
        <TooltipProvider>
            <aside
                className={cn(
                    'flex flex-col border-r bg-card transition-all duration-300',
                    sidebarCollapsed ? 'w-16' : 'w-64'
                )}
            >
                {/* Logo Header */}
                <div className={cn(
                    'flex h-16 items-center border-b px-4',
                    sidebarCollapsed ? 'justify-center' : 'justify-between'
                )}>
                    {!sidebarCollapsed && (
                        <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
                                M
                            </div>
                            <span className="text-lg font-semibold">MTOMICS MDM</span>
                        </div>
                    )}
                    {sidebarCollapsed && (
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
                            M
                        </div>
                    )}
                </div>

                {/* Main Navigation */}
                <nav className="flex-1 space-y-1 p-2">
                    {filteredMainNavItems.map((item) => (
                        <NavItem
                            key={item.path}
                            item={item}
                            isActive={isActive(item.path)}
                            isCollapsed={sidebarCollapsed}
                        />
                    ))}
                </nav>

                <Separator />

                {/* Bottom Navigation */}
                <nav className="space-y-1 p-2">
                    {filteredBottomNavItems.map((item) => (
                        <NavItem
                            key={item.path}
                            item={item}
                            isActive={isActive(item.path)}
                            isCollapsed={sidebarCollapsed}
                        />
                    ))}
                </nav>

                {/* Collapse Toggle */}
                <div className={cn(
                    'border-t p-2',
                    sidebarCollapsed ? 'flex justify-center' : ''
                )}>
                    <Button
                        variant="ghost"
                        size={sidebarCollapsed ? 'icon' : 'sm'}
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className={cn(
                            'w-full justify-start gap-2',
                            sidebarCollapsed && 'justify-center'
                        )}
                    >
                        {sidebarCollapsed ? (
                            <ChevronRight className="h-4 w-4" />
                        ) : (
                            <>
                                <ChevronLeft className="h-4 w-4" />
                                <span>Collapse</span>
                            </>
                        )}
                    </Button>
                </div>
            </aside>
        </TooltipProvider>
    );
}
